import {
  Controller,
  FieldPath,
  FieldValues,
  UseFormReturn,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import NumberFormat, { NumberFormatProps } from "react-number-format";

export const Label = ({ children }: { children: React.ReactNode }) => {
  return (
    <label className="block space-y-8 text-xl font-bold">{children}</label>
  );
};
export const HelperText = ({ children }: { children: React.ReactNode }) => {
  return <div className="text-base font-normal italic">{children}</div>;
};
export const InputField = <T extends FieldValues>({
  label,
  formProps,
  children,
  errorMessage,
  helpText,
  name,
}: {
  label: React.ReactNode;
  errorMessage?: React.ReactNode;
  formProps: UseFormReturn<T>;
  children: React.ReactNode;
  helpText?: React.ReactNode;
  name: FieldPath<T>;
}) => {
  const { t } = useTranslation();
  const error = formProps.getFieldState(name, formProps.formState).error;
  return (
    <Label>
      <div>
        <div>{label}</div>
        {helpText && <HelperText>{helpText} </HelperText>}
      </div>
      {children}
      {error && (
        <div className="text-base font-normal text-venue-highlight">
          {errorMessage || error.message || t("genericErrorMessage")}
        </div>
      )}
    </Label>
  );
};

export const TextField = <T extends FieldValues>({
  label,
  formProps,
  name,
  helpText,
  ...props
}: {
  label: React.ReactNode;
  formProps: UseFormReturn<T>;
  helpText?: React.ReactNode;
  name: FieldPath<T>;
} & React.ComponentPropsWithoutRef<"input">) => {
  return (
    <InputField
      helpText={helpText}
      label={label}
      formProps={formProps}
      name={name}
    >
      <input
        {...formProps.register(name)}
        className="block w-full rounded-venue-brand bg-venue-tertiary p-12 shadow-venue-brand-style disabled:cursor-not-allowed"
        {...props}
      />
    </InputField>
  );
};

export const TextAreaField = <T extends FieldValues>({
  label,
  formProps,
  name,
  helpText,
  ...props
}: {
  label: React.ReactNode;
  formProps: UseFormReturn<T>;
  name: FieldPath<T>;
  helpText?: React.ReactNode;
} & React.ComponentPropsWithoutRef<"textarea">) => {
  return (
    <InputField
      helpText={helpText}
      label={label}
      formProps={formProps}
      name={name}
    >
      <textarea
        {...formProps.register(name)}
        className="block w-full rounded-venue-brand bg-venue-tertiary p-12 shadow-venue-brand-style disabled:cursor-not-allowed"
        {...props}
      />
    </InputField>
  );
};

export const FormattedCurrencyField = <T extends FieldValues>({
  label,
  formProps,
  onChange,
  name,
  helpText,
  ...props
}: {
  label: React.ReactNode;
  formProps: UseFormReturn<T>;
  name: FieldPath<T>;
  helpText?: React.ReactNode;
  onChange?: (value: number | undefined) => void;
} & Pick<NumberFormatProps, "decimalScale"> &
  Pick<
    React.InputHTMLAttributes<HTMLInputElement>,
    "onBlur" | "onFocus" | "onKeyDown" | "onMouseUp" | "placeholder"
  >) => {
  const { t } = useTranslation();
  return (
    <InputField
      label={label}
      helpText={helpText}
      formProps={formProps}
      name={name}
    >
      <Controller
        control={formProps.control}
        name={name}
        render={({ field, fieldState: _ }) => (
          <>
            <NumberFormat
              value={field.value}
              prefix={t("currencySymbol")}
              displayType="input"
              className="block w-full rounded-venue-brand bg-venue-tertiary p-12 text-right shadow-venue-brand-style disabled:cursor-not-allowed"
              thousandSeparator={true}
              decimalScale={2}
              onBlur={() => {
                console.log("blurred");
                field.onBlur();
              }}
              onValueChange={(target) => {
                field.onChange(target.floatValue);
                onChange?.(target.floatValue);
              }}
              {...props}
            />
          </>
        )}
      />
    </InputField>
  );
};
